<template>
  <div>
    <gat-grid
      title="Proforma"
      class="table-spacing elevation-0"
      :columns="columns"
      :items="portcallProformas"
      :fixedheader="true"
      :selectable="true"
      :noSelectAll="true"
      keyName="ID"
      @row-clicked="proformaClicked"
      :selectedKeys="selectedProformaIds"
      @selectionChanged="proformaSelected($event)"
      :height="callId ? 'calc(100vh - 135px)' : 'calc(100vh - 110px)'"
      reloadBtn
      @reload-clicked="getProformas(callId)"
      :dense="true"
      :loading="isLoading">
      <template slot="btns">
        <WmButtonGroup
          class="right mr-1"
          :buttons="wmButtonGroup"
          @createEmailClick="createEmailClick($event)"
          @prepareProformaPortcallConnectClick="prepareProformaPortcallConnect($event)"
          @prepareExportProformaClick="prepareExportProformaToExpense()" />
      </template>
    </gat-grid>
    <div class="pb-6 mb-6">
      <gat-dialog
        v-model="connectProformaPortcallDialogVisible"
        :cancel-button-disabled="isSaving"
        cancelButtonCaption="CANCEL"
        okButtonCaption="OK"
        :auto-close="false"
        width="1000"
        @okClick="connectProformaToPortcall()"
        @cancelClick="isSaving = false"
        :okButtonDisabled="isSaving || !userCanConnectProformaPortcall || !(selectedImportProformaIds.length === 1)">
        <span class="text-h6 grey--text text--darken-1">
          {{ `Select a proforma to import to portcall (${callDetails.PORTCALL_NUMBER})` }}
        </span>
        <div style="max-height: 600px; overflow-y: auto">
          <gat-grid
            :columns="newProformaColumns"
            :items="agencyProformas"
            :fixedheader="true"
            :selectable="true"
            :noSelectAll="true"
            keyName="PROFORMA_ID"
            :dense="true"
            @selectionChanged="importProformaSelected($event)"
            :selectedKeys="selectedImportProformaIds"
            :rowclass="importProformaRowClass"
            :loading="isLoading">
          </gat-grid>
        </div>
        <v-overlay :value="isSaving" absolute>
          <v-progress-circular indeterminate color="primary" />
        </v-overlay>
      </gat-dialog>
    </div>
    <div class="pb-6 mb-6">
      <gat-dialog
        v-model="exportProformaToExpenseDialogVisible"
        :cancel-button-disabled="isSaving"
        cancelButtonCaption="CANCEL"
        okButtonCaption="OK"
        :auto-close="false"
        width="1000"
        @okClick="exportProformaToExpense()"
        @cancelClick="isSaving = false"
        :okButtonDisabled="isSaving || !userCanExportProforma || !(selectedExportProformaLinesId.length > 0)">
        <div class="d-flex align-center justify-space-between">
          <span class="text-h6 grey--text text--darken-1">Select proforma lines to create expenses</span>
          <v-checkbox
            v-model="mergeProformaLines"
            label="Merge proforma lines"
            dense
            hide-details
            class="ma-0"
            @change="updateSelectedExportProformaLinesId" />
        </div>
        <div style="max-height: 600px; overflow-y: auto">
          <gat-grid
            :columns="proformaLineColumns"
            :items="mergeProformaLines ? exportableMergedProformaLines : exportableProformaLines"
            :fixedheader="true"
            :selectable="true"
            keyName="ID"
            :dense="true"
            @selectionChanged="exportableProformaLineSelected($event)"
            :selectedKeys="selectedExportProformaLinesId"
            :rowclass="exportProformaLineRowClass"
            :options="{
              checkboxIsHidden,
            }"
            :loading="isLoading">
            <template #cell="{ column, row, value }">
              <template v-if="(column.field === 'PRICE' || column.field === 'TOTAL_SUM') && (+value || +value === 0)">
                <div>
                  <span>{{ row.CURRENCY }} {{ (+value).toFixed(2) }}</span>
                </div>
              </template>
              <template v-else-if="column.field === 'BUDGET'">
                <div>
                  <span>{{ !!value ? 'Yes' : 'No' }}</span>
                </div>
              </template>
              <span v-else>{{ value }}</span>
            </template>
          </gat-grid>
        </div>
        <v-overlay :value="isSaving" absolute>
          <v-progress-circular indeterminate color="primary" />
        </v-overlay>
      </gat-dialog>
    </div>
    <EmailGenerator
      ref="emailGenerator"
      :portcallId="callId"
      location="portcall"
      :proforma-id="selectedProformaId"
      @isLoading="isLoading = $event"
      @isLoadingOverlay="isLoading = $event" />
  </div>
</template>

<script setup lang="ts">
// eslint-disable-next-line import/no-cycle
import router from '@/router/router';
import { apiGet, apiPost } from '@/store/api';
import { usePortcallStore } from '@/store/portcallStore';
import { useAppStatusStore } from '@/store/appStatusStore';
import { useApplicationStore } from '@/store/applicationStore';
import { GlobalHelperFunctions } from '@/common/GlobalHelperFunctions';
import WmButtonGroup from '@/app-components/WmButtonGroup.vue';
import EmailGenerator from '@/app-components/EmailGenerator.vue';
import { computed, nextTick, ref, watch } from 'vue';
import { useHelperStore } from '@/store/helperStore';
import { ColumnDef } from '@/types/ColumnDef';
import { EnquiryListItem, ProformaListItem, ProformaExpenseLine, ProformaEmailTemplate } from '@/types/ProformaTypes';
import { getAgencyProformas } from '@/services/api/api-calls/getAgencyProformas';
import { connectProformaPortcall } from '@/services/api/api-calls/connectProformaPortcall';
import { GatResponseResultCategory } from '@/types/GatResponseResult';
import { useProformaStore } from '../stores/proformaStore';

interface ProformaListProps {
  callId: string;
  location: string;
  parentId?: number | string;
}

const props = defineProps<ProformaListProps>();
const proformaStore = useProformaStore();
const appStatusStore = useAppStatusStore();
const applicationStore = useApplicationStore();
const portcallStore = usePortcallStore();
const helperStore = useHelperStore();

const isLoading = ref(false);
const isSaving = ref(false);
const agencyProformas = ref<EnquiryListItem[]>([]);
const portcallProformas = computed(() => proformaStore.portcallProformas);
const exportableProformaLines = ref<ProformaExpenseLine[]>([]);
const exportableMergedProformaLines = ref<ProformaExpenseLine[]>([]);
const connectProformaPortcallDialogVisible = ref(false);
const exportProformaToExpenseDialogVisible = ref(false);
const selectedProformaIds = ref<number[]>([]);
const selectedImportProformaIds = ref<number[]>([]);
const selectedExportProformaLinesId = ref<number[]>([]);
const callDetails = computed(() => portcallStore.callDetails);
const emailGenerator = ref<{ generateEmail: (template: ProformaEmailTemplate) => Promise<void> }>();
const agencyId = computed(() => callDetails.value.SETUP_ID);
const selectedProformaHasClientNumber = ref(false);
const emailTemplateItems = computed(() => {
  const templates = [...proformaStore.emailTemplates];
  let result: any[] = [];
  const ignoreGroupMinCount = applicationStore.hasFeatureCode('IGNORE_DOCUMENT_GROUPING_MIN_COUNT');
  if (Array.isArray(templates) && templates.length > 0) {
    if (ignoreGroupMinCount ? true : templates.length > 10) {
      result = helperStore.emailTemplatesGroupedByLabels(templates, false);
    } else {
      result = templates.map((item) => ({
        caption: item.TITLE,
        eventName: 'createEmailClick',
        icon: 'mdi-file-outline',
        ID: item.ID,
      }));
    }
  }
  return result;
});

const validPortcallStatusToImportExportProforma = computed(() => {
  const status = callDetails.value.STATUS;
  let callCategory;
  if (callDetails.value.extra.PCE_CALLTYPE) {
    const callType = helperStore.callTypes.find((item) => item.value == callDetails.value.extra.PCE_CALLTYPE);
    callCategory = callType?.PCT_CATEGORY;
  }

  return (status === 0 || status === 1 || status === 2 || status === 3) && !callCategory;
});

const userCanConnectProformaPortcall = computed(
  () =>
    applicationStore.user.internal &&
    (applicationStore.userRight('PR.NEW') || applicationStore.userRight('PR.NEW_FROM_PORTCALL')) &&
    validPortcallStatusToImportExportProforma.value
);

const userCanExportProforma = computed(
  () =>
    applicationStore.user.internal &&
    applicationStore.userRight('EX.NEW') &&
    validPortcallStatusToImportExportProforma.value
);

const userCanGenerateDoc = computed<boolean>(() => {
  const isInternal = applicationStore.user.internal;
  if (isInternal) {
    return applicationStore.userRight('DOC.NEW_AND_EDIT');
  }
  // const externalRights = applicationStore.userRight('DOC.GENERATE_DOCUMENT');
  // return externalRights;
  return false;
});

const selectedProformaId = computed<number>(() => {
  if (selectedProformaIds.value.length === 0) {
    return 0;
  }
  return selectedProformaIds.value[0];
});

const applicationStatus = computed(() => {
  const result = appStatusStore.getAppStatus;
  if (selectedProformaId.value) {
    result.ProformaId = selectedProformaId.value;
  }
  return result;
});

const mergeProformaLinesSavedInfoKey = 'web_merge_proforma_lines';
const mergeProformaLines = ref<boolean>(false);
let isMergeProformaLinesLoaded = false;
apiGet(`savedInfo/${mergeProformaLinesSavedInfoKey}`).then((data) => {
  if (data.length > 0) {
    mergeProformaLines.value = data[0].INTVALUE1 === 1;
  }
  isMergeProformaLinesLoaded = true;
});
watch(mergeProformaLines, async (value) => {
  if (isMergeProformaLinesLoaded) {
    const savedInfo = {
      TypeName: mergeProformaLinesSavedInfoKey,
      Name: applicationStore.user.userName,
      IntValue: value ? 1 : 0,
    };
    await apiPost('savedInfo', savedInfo);
  }
});

const wmButtonGroup = computed(() => {
  const result = [];
  result.push({
    caption: 'Import proforma',
    icon: 'mdi-file-import',
    agencyId: agencyId.value,
    location: 'portcall',
    foreignKey: props.callId,
    applicationStatus: applicationStatus.value,
    eventName: 'prepareProformaPortcallConnectClick',
    disabled: !userCanConnectProformaPortcall.value || selectedProformaIds.value.length > 0,
  });
  result.push({
    caption: 'Create expense',
    icon: 'mdi-file-export',
    agencyId: agencyId.value,
    location: 'portcall',
    foreignKey: props.callId,
    applicationStatus: applicationStatus.value,
    eventName: 'prepareExportProformaClick',
    disabled:
      !userCanExportProforma.value || selectedProformaIds.value.length !== 1 || !selectedProformaHasClientNumber.value,
  });
  result.push({
    btnType: 'CREATE_DOC',
    docType: 2,
    agencyId: agencyId.value,
    location: 'portcall',
    foreignKey: props.callId,
    applicationStatus: applicationStatus.value,
    disabled: !userCanGenerateDoc.value || selectedProformaId.value === 0,
  });
  result.push({
    btnType: 'CUSTOM',
    caption: 'Create email',
    icon: 'mdi-email',
    menuItems: emailTemplateItems.value,
    disabled: emailTemplateItems.value.length === 0 || selectedProformaId.value === 0,
  });
  return result;
});

const columns = computed(() => {
  const result: ColumnDef<ProformaListItem>[] = [
    { header: 'No', field: 'PROFORMA_NUMBER' },
    // { header: 'Client name', field: 'clientName' },
    // { header: 'Sum', field: 'sum', dataType: 'currency', decimals: 2 },
    { header: 'Created date', field: 'CREATED_DATE', dataType: 'date' },
    { header: 'Vessel name', field: 'VESSEL_NAME', nowrap: true },
    { header: 'Client name', field: 'CLIENT_NAME', nowrap: true },
    { header: 'Client number', field: 'CLIENT_NUMBER', nowrap: true },
    { header: 'Amount', field: 'SUBTOTAL_IN', width: '1%', dataType: 'currency', decimals: 2 },
    // {
    //   header: 'Discount',
    //   field: 'discount',
    //   dataType: 'currency',
    //   decimals: 2,
    // },
    // {
    //   header: 'Surcharge',
    //   field: 'surcharge',
    //   dataType: 'currency',
    //   decimals: 2,
    // },
    // { header: 'VAT', field: 'vat', dataType: 'currency', decimals: 2 },
    // { header: 'Total', field: 'total', dataType: 'currency', decimals: 2 },
    { header: 'Comment', width: '100%', field: 'COMMENTS_NEW' },
  ];
  return result;
});

const newProformaColumns = computed(() => {
  const result: ColumnDef<EnquiryListItem>[] = [
    { header: 'Proforma no', field: 'PROFORMA_NUMBER', nowrap: true },
    { header: 'Created date', field: 'CREATED_DATE', nowrap: true },
    { header: 'Vessel', field: 'VESSEL_NAME', nowrap: true },
    { header: 'Harbour', field: 'HNAME', nowrap: true },
    { header: 'Total', field: 'TOTAL', dataType: 'number', nowrap: true },
    { header: 'Client', field: 'CLIENT_NAME', nowrap: true },
  ];
  return result;
});

const proformaLineColumns = computed(() => {
  const result: ColumnDef<ProformaExpenseLine>[] = [
    { header: 'Text', field: 'EXPENSE_TEXT', nowrap: true },
    { header: 'Quantity', field: 'QUANTITY', dataType: 'number', nowrap: true },
    { header: 'Price', field: 'PRICE', dataType: 'number', nowrap: true },
    { header: 'Vat %', field: 'VAT', nowrap: true },
    { header: 'Total', field: 'TOTAL_SUM', dataType: 'number', nowrap: true },
    { header: 'Account code', field: 'ACCOUNT_CODE', nowrap: true },
    { header: 'Additional info', field: 'MISC', nowrap: true },
    { header: 'Budget?', field: 'BUDGET', nowrap: true },
    { header: 'Client', field: 'CLIENT_NAME', nowrap: true },
    { header: 'Supplier', field: 'SUPPLIER_NAME', nowrap: true },
    { header: 'Expense group', field: 'EXPENSE_GROUP_NAME', nowrap: true },
    { header: 'Expense template', field: 'EXPENSE_TEMPLATE_NAME', nowrap: true },
    { header: 'Price group', field: 'PRICE_GROUP_NAME', nowrap: true },
  ];
  return result;
});

async function getProformas(portcallId: string) {
  isLoading.value = true;
  try {
    await proformaStore.fetchPortcallProformas(portcallId);
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
}

function proformaSelected(proformaIDs: number[]) {
  selectedProformaIds.value = [];
  if (proformaIDs.length >= 1) {
    const proformaId = proformaIDs[proformaIDs.length - 1];
    selectedProformaIds.value.push(proformaId);
    const selectedProforma = portcallProformas.value.find((proforma) => +proforma.ID === proformaId);
    if (selectedProforma) {
      selectedProformaHasClientNumber.value = !!selectedProforma.CLIENT_NUMBER;
    } else {
      selectedProformaHasClientNumber.value = false;
    }
  } else {
    selectedProformaHasClientNumber.value = false;
  }
}

function importProformaRowClass(item: EnquiryListItem) {
  return item.VESSEL_ID === callDetails.value.VESSEL_ID ? 'font-weight-bold' : '';
}

function exportProformaLineRowClass(item: ProformaExpenseLine) {
  return item.EXPORTABLE === 0 ? 'red--text' : '';
}

function importProformaSelected(proformaIDs: number[]) {
  selectedImportProformaIds.value = [];
  if (proformaIDs.length > 1) {
    selectedImportProformaIds.value.push(proformaIDs[proformaIDs.length - 1]);
  }
  if (proformaIDs.length === 1) {
    selectedImportProformaIds.value.push(proformaIDs[0]);
  }
}

function exportableProformaLineSelected(proformaLinesID: number[]) {
  selectedExportProformaLinesId.value = [];
  selectedExportProformaLinesId.value.push(...proformaLinesID);
}

function proformaClicked(rowItem: ProformaListItem) {
  const params = {
    callId: props.callId,
    proformaId: rowItem.ID,
  };
  router.push({ name: 'proformaItem', params });
}

function checkboxIsHidden(item: ProformaExpenseLine) {
  if (item.EXPORTABLE === 1) {
    return false;
  }
  return true;
}

async function prepareProformaPortcallConnect(item: any) {
  try {
    isLoading.value = true;
    agencyProformas.value = [];
    selectedImportProformaIds.value = [];
    const today = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(today.getFullYear() - 1);
    const fromDateTime = oneYearAgo.toISOString();
    const toDateTime = today.toISOString();
    const result = await getAgencyProformas(item.agencyId, fromDateTime, toDateTime, false);
    if (result.resultCategory === GatResponseResultCategory.Success && result.payload) {
      const unsorted = result.payload.proformas;
      const sorted = unsorted.sort((a, b) => b.PROFORMA_NUMBER.localeCompare(a.PROFORMA_NUMBER));
      agencyProformas.value = sorted;
    }
    connectProformaPortcallDialogVisible.value = true;
  } catch {
    connectProformaPortcallDialogVisible.value = false;
  } finally {
    isLoading.value = false;
  }
}

async function connectProformaToPortcall() {
  try {
    isSaving.value = true;
    if (props.callId && selectedImportProformaIds.value.length === 1) {
      const result = await connectProformaPortcall(
        props.callId,
        selectedImportProformaIds.value[0],
        callDetails.value.SEDNA_JOB_ID
      );
      if (
        result.resultCategory === GatResponseResultCategory.Warning ||
        result.resultCategory === GatResponseResultCategory.Error
      ) {
        window.messageBus.$emit('showMessage', {
          type: result.resultCategory === GatResponseResultCategory.Warning ? 'warning' : 'error',
          text: result.message || 'A problem occurred while connecting the proforma to portcall.',
          icon: true,
        });
      } else {
        await getProformas(props.callId);
      }
    }
  } finally {
    isSaving.value = false;
    connectProformaPortcallDialogVisible.value = false;
  }
}

function mergeProformaExpenseLines(proformaLines: ProformaExpenseLine[]) {
  const mergedLines: ProformaExpenseLine[] = [];
  const mergedLineByKey = new Map<string, ProformaExpenseLine>();

  proformaLines.forEach((line) => {
    const key = JSON.stringify([
      line.VAT,
      line.CURRENCY,
      line.ACCOUNT_CODE,
      line.EXPENSE_GROUP_ID,
      line.GROUP_TEXT,
      line.EXPORTABLE,
    ]);
    const mergedLine = mergedLineByKey.get(key);

    if (!mergedLine) {
      const newMergedLine = { ...line };
      mergedLineByKey.set(key, newMergedLine);
      mergedLines.push(newMergedLine);
      return;
    }

    const netAmount = (mergedLine.NET_AMOUNT ?? 0) + (line.NET_AMOUNT ?? 0);
    const grossAmount = (mergedLine.AMOUNT ?? 0) + (line.AMOUNT ?? 0);
    mergedLine.QUANTITY = 1;
    mergedLine.NET_AMOUNT = netAmount;
    mergedLine.PRICE = netAmount;
    mergedLine.AMOUNT = grossAmount;
    mergedLine.TOTAL_SUM = grossAmount;
    mergedLine.ORIGINAL_BUDGET = grossAmount;
    mergedLine.EXPENSE_TEXT = mergedLine.GROUP_TEXT;
    mergedLine.MISC = mergedLine.MISC ? `${mergedLine.MISC} ${line.MISC}` : line.MISC;
    if ((mergedLine.EXPENSE_TEMPLATE_ID ?? 0) < 1) mergedLine.EXPENSE_TEMPLATE_ID = line.EXPENSE_TEMPLATE_ID;
  });

  return mergedLines;
}

function updateSelectedExportProformaLinesId() {
  selectedExportProformaLinesId.value = (
    mergeProformaLines.value ? exportableMergedProformaLines.value : exportableProformaLines.value
  )
    .filter((line) => line.EXPORTABLE === 1 && !!line.AMOUNT)
    .map((line) => line.ID);
}

async function prepareExportProformaToExpense() {
  try {
    isLoading.value = true;
    selectedExportProformaLinesId.value = [];
    const result = await apiGet(`proforma/${selectedProformaId.value}/proforma-expense-lines`);
    if (result.resultCategory === GatResponseResultCategory.Success && result.payload.length > 0) {
      exportableProformaLines.value = [...result.payload];
      exportableMergedProformaLines.value = mergeProformaExpenseLines([...result.payload]);
    }
    exportProformaToExpenseDialogVisible.value = true;
    await nextTick();
    updateSelectedExportProformaLinesId();
  } catch {
    exportProformaToExpenseDialogVisible.value = false;
  } finally {
    isLoading.value = false;
  }
}

async function exportProformaToExpense() {
  try {
    isSaving.value = true;
    const exportablePrLines = mergeProformaLines.value
      ? exportableMergedProformaLines.value
      : exportableProformaLines.value;
    if (selectedProformaId.value && selectedExportProformaLinesId.value.length > 0 && exportablePrLines.length > 0) {
      const exportableLines = exportablePrLines.filter(
        (line) =>
          selectedExportProformaLinesId.value.includes(line.ID) &&
          line.PROFORMA_ID === selectedProformaId.value &&
          line.EXPORTABLE === 1
      );
      if (exportableLines.length > 0) {
        const linesWithHistory = exportableLines.map((line: ProformaExpenseLine) => {
          GlobalHelperFunctions.setHistoryField(line, 'HISTORY');
          return line;
        });
        const result = await apiPost(`proforma/${selectedProformaId.value}/create-expense`, linesWithHistory);
        if (result.resultCategory === GatResponseResultCategory.Success) {
          window.messageBus.$emit('showMessage', {
            type: 'success',
            text: `${result.payload?.successfulImports ?? 0} proforma line(s) successfully exported to expenses.`,
            icon: true,
          });
        } else if (
          result.resultCategory === GatResponseResultCategory.Warning ||
          result.resultCategory === GatResponseResultCategory.Error
        ) {
          window.messageBus.$emit('showMessage', {
            type: result.resultCategory === GatResponseResultCategory.Warning ? 'warning' : 'error',
            text: result.message || 'An error occurred while importing proforma to expense.',
            icon: true,
          });
        }
      }
    }
  } finally {
    isSaving.value = false;
    exportProformaToExpenseDialogVisible.value = false;
  }
}

async function createEmailClick(item: ProformaEmailTemplate) {
  const template = proformaStore.emailTemplates.find((entry) => entry.ID === item.ID);
  if (props.callId && selectedProformaId.value && template) {
    await emailGenerator.value?.generateEmail(template);
  }
}

watch(
  () => props.callId,
  (newValue) => {
    if (newValue) {
      getProformas(newValue);
    }
  },
  { immediate: true }
);
</script>
